// src/utils/auth.js

/**
 * Constants for token storage keys and API endpoints
 */
const TOKEN_STORAGE = {
  ACCESS: "access",
  REFRESH: "refresh",
};

const API_ENDPOINTS = {
  VERIFY: "/token/verify/",
  REFRESH: "/token/refresh/",
};

/**
 * Storage utility to handle token persistence
 */
const tokenStorage = {
  setToken: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  },

  getToken: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  },

  removeToken: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from localStorage:", error);
    }
  },

  clearTokens: () => {
    try {
      Object.values(TOKEN_STORAGE).forEach((key) =>
        localStorage.removeItem(key)
      );
    } catch (error) {
      console.error("Error clearing tokens from localStorage:", error);
    }
  },
};

/**
 * API client for token-related operations
 */
class TokenAPI {
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  async makeRequest(endpoint, payload) {
    try {
      // console.log("Making request:", endpoint, payload);
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      return await response.json();
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  verifyToken(token) {
    return this.makeRequest(API_ENDPOINTS.VERIFY, { token });
  }

  refreshToken(token) {
    return this.makeRequest(API_ENDPOINTS.REFRESH, { refresh: token });
  }
}

/**
 * Main authentication utility class
 */
export class AuthManager {
  constructor(baseURL) {
    this.tokenAPI = new TokenAPI(baseURL);
  }

  /**
   * Initialize authentication state by verifying and refreshing tokens if necessary
   * @returns {Promise<{ status: boolean, error?: string }>}
   */
  async initialize() {
    try {
      const accessToken = tokenStorage.getToken(TOKEN_STORAGE.ACCESS);
      const refreshToken = tokenStorage.getToken(TOKEN_STORAGE.REFRESH);

      if (!accessToken && !refreshToken) {
        return { status: false };
      }

      // Verify current access token
      const verifyResult = await this.tokenAPI.verifyToken(accessToken);
      if (!verifyResult?.detail) {
        return { status: true };
      }

      // Try refreshing token if verification fails
      const refreshResult = await this.tokenAPI.refreshToken(refreshToken);

      if (refreshResult?.code === "token_not_valid") {
        tokenStorage.clearTokens();
        // localStorage.clear();
        // window.location.href = '/login';
        return {
          status: false,
          error: "Invalid refresh token",
        };
      }

      if (refreshResult?.access) {
        tokenStorage.setToken(TOKEN_STORAGE.ACCESS, refreshResult.access);
        return { status: true };
      }
      return {
        status: false,
        error: "Failed to refresh token",
      };
    } catch (error) {
      console.error("Auth initialization error:", error);
      return {
        status: false,
        error: "Authentication error",
      };
    }
  }

  /**
   * Set new tokens after successful login
   * @param {string} accessToken
   * @param {string} refreshToken
   */
  setTokens(accessToken, refreshToken) {
    tokenStorage.setToken(TOKEN_STORAGE.ACCESS, accessToken);
    tokenStorage.setToken(TOKEN_STORAGE.REFRESH, refreshToken);
  }

  /**
   * Clear all authentication tokens
   */
  logout() {
    tokenStorage.clearTokens();
  }

  /**
   * Get current access token
   * @returns {string|null}
   */
  getAccessToken() {
    return tokenStorage.getToken(TOKEN_STORAGE.ACCESS);
  }
}
